import { Search, Trash2, UserPlus } from 'lucide-react'
import { type MouseEvent, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  SortableTableHead,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TableSkeletonRows } from '@/components/ui/table-skeleton'
import { SalesOwnerFilter } from '../../../../components/SalesOwnerFilter'
import { StatusPill } from '../../../../components/StatusPill'
import { isProduction } from '../../../../config/config'
import { useDebouncedInput } from '../../../../hooks/useDebouncedInput'
import { setStoredCustomerListFilters } from '../../../../lib/customerListFilters'
import { cn } from '../../../../lib/utils'
import {
  CustomerSortColumn,
  orgPillStatus,
  OrgStatus,
  useCurrentUser,
  useDeleteCustomer,
  useOrganisations,
} from '../../../../models/user'
import { AddCustomerDialog } from './AddCustomerDialog'
import { CustomerListCard, NAME_COLUMN_CLASS, STATUS_COLUMN_CLASS } from './CustomerListCard'
import { nextCustomerSort, parseCustomerSort } from './customerListSort'
import { CustomerPolicyCells } from './CustomerPolicyCells'

const ORG_STATUS_LABELS: Record<OrgStatus, string> = {
  [OrgStatus.ONBOARDING]: 'Onboarding',
  [OrgStatus.QUOTING]: 'Quoting',
  [OrgStatus.UNDERWRITING]: 'Underwriting',
  [OrgStatus.ACTIVE]: 'Active',
}

interface DeleteCustomerButtonProps {
  orgId: string
  orgName: string
  onDelete: (id: string, name: string) => void | Promise<void>
}

function DeleteCustomerButton({ orgId, orgName, onDelete }: DeleteCustomerButtonProps) {
  return (
    <ConfirmationDialog
      title={`Delete ${orgName}?`}
      description="This permanently removes the customer, motor carrier, application, trucks, drivers, claims and all related data — plus their user accounts. Dev/staging only."
      confirmLabel="Delete customer"
      onConfirm={() => onDelete(orgId, orgName)}
    >
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={`Delete ${orgName}`}
        title="Delete customer"
        className="text-destructive hover:bg-destructive/10"
      >
        <Trash2 className="size-4" />
      </Button>
    </ConfirmationDialog>
  )
}

export function InsuranceCustomers() {
  const navigate = useNavigate()
  const [addDialogOpen, setAddDialogOpen] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const page = Number(searchParams.get('page') || '1')
  const search = searchParams.get('search') || ''
  const salesOwner = searchParams.get('sales_owner') || ''
  const status = searchParams.get('status') || ''
  const sort = searchParams.get('sort') || ''
  const activeSort = parseCustomerSort(sort)

  const buildParams = (overrides: {
    search?: string
    salesOwner?: string
    status?: string
    sort?: string
    page?: number
  }) => {
    const next = { search, salesOwner, status, sort, page: 1, ...overrides }
    const params: Record<string, string> = {}
    if (next.search) params.search = next.search
    if (next.salesOwner) params.sales_owner = next.salesOwner
    if (next.status) params.status = next.status
    if (next.sort) params.sort = next.sort
    if (next.page > 1) params.page = String(next.page)
    return params
  }

  function updateParams(overrides: {
    search?: string
    salesOwner?: string
    status?: string
    sort?: string
    page?: number
  }) {
    setSearchParams(buildParams(overrides))
  }

  const { inputValue, setInputValue } = useDebouncedInput(search, (value) => {
    updateParams({ search: value })
  })

  const { organisations, count, hasNextPage, hasPreviousPage, isLoading, isFetching } = useOrganisations({
    page,
    search,
    salesOwner: salesOwner || undefined,
    status: status || undefined,
    ordering: sort || undefined,
    keepPrevious: true,
  })
  // Kept so the customer page's way back lands on the list you left, not a reset one.
  const { user } = useCurrentUser({ enabled: true })
  useEffect(() => {
    setStoredCustomerListFilters(user?.id, searchParams.toString())
  }, [searchParams, user?.id])

  const deleteCustomer = useDeleteCustomer()
  const canDelete = !isProduction
  const columnCount = canDelete ? 8 : 7
  // `inert`, not pointer-events: held-over rows must be unreachable by keyboard too.
  const staleRows = isFetching && !isLoading
  const staleRowsClass = cn('transition-opacity', staleRows && 'opacity-60')

  async function handleDelete(orgId: string, name: string) {
    try {
      await deleteCustomer.mutateAsync(orgId)
      toast.success(`Deleted customer "${name}"`)
    } catch {
      toast.error('Failed to delete customer')
    }
  }

  function goToPage(p: number) {
    updateParams({ page: p })
  }

  function setSalesOwner(value: string) {
    updateParams({ salesOwner: value && value !== 'all' ? value : '' })
  }

  function setStatus(value: string) {
    updateParams({ status: value && value !== 'all' ? value : '' })
  }

  function sortDirection(column: CustomerSortColumn) {
    return activeSort?.column === column ? activeSort.direction : undefined
  }

  function sortBy(column: CustomerSortColumn) {
    updateParams({ sort: nextCustomerSort(sort, column) })
  }

  function openCustomer(orgId: string) {
    navigate(`/insure/internal/customers/${orgId}`)
  }

  function openCustomerInNewTab(orgId: string) {
    window.open(`/insure/internal/customers/${orgId}`, '_blank', 'noopener,noreferrer')
  }

  // Row click opens the customer; cmd/ctrl/shift-click and middle-click open
  // the detail in a new tab (rows aren't anchors, so we handle it explicitly).
  function handleRowClick(e: MouseEvent, orgId: string) {
    if (e.metaKey || e.ctrlKey || e.shiftKey) {
      openCustomerInNewTab(orgId)
    } else {
      openCustomer(orgId)
    }
  }

  return (
    <>
      <CustomerListCard
        action={
          <Button onClick={() => setAddDialogOpen(true)}>
            <UserPlus className="size-4" />
            Add user
          </Button>
        }
        filters={
          <>
            <div className="relative w-full sm:max-w-sm sm:flex-1">
              <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                placeholder="Search by name, carrier or legal name, DOT/MC, or user..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="h-11 border-neutral-300 pl-9 text-sm"
              />
            </div>

            <SalesOwnerFilter
              value={salesOwner}
              onValueChange={setSalesOwner}
              className="w-full border-neutral-300 data-[size=default]:h-11 sm:w-[180px]"
            />

            <Select value={status || 'all'} onValueChange={setStatus}>
              <SelectTrigger className="w-full border-neutral-300 data-[size=default]:h-11 sm:w-[180px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {Object.values(OrgStatus).map((value) => (
                  <SelectItem key={value} value={value}>
                    {ORG_STATUS_LABELS[value]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </>
        }
      >
        <Table>
          <TableHeader>
            <TableRow>
              <SortableTableHead
                className={cn('font-semibold', NAME_COLUMN_CLASS)}
                direction={sortDirection(CustomerSortColumn.NAME)}
                onSort={() => sortBy(CustomerSortColumn.NAME)}
              >
                Name
              </SortableTableHead>
              <SortableTableHead
                className={cn('font-semibold', STATUS_COLUMN_CLASS)}
                direction={sortDirection(CustomerSortColumn.STATUS)}
                onSort={() => sortBy(CustomerSortColumn.STATUS)}
              >
                Status
              </SortableTableHead>
              <SortableTableHead
                className="font-semibold"
                direction={sortDirection(CustomerSortColumn.USERS)}
                onSort={() => sortBy(CustomerSortColumn.USERS)}
              >
                Users
              </SortableTableHead>
              <TableHead className="font-semibold">Policies</TableHead>
              <SortableTableHead
                className="font-semibold"
                direction={sortDirection(CustomerSortColumn.BOUND)}
                onSort={() => sortBy(CustomerSortColumn.BOUND)}
              >
                Bound
              </SortableTableHead>
              <SortableTableHead
                className="font-semibold"
                direction={sortDirection(CustomerSortColumn.STARTS)}
                onSort={() => sortBy(CustomerSortColumn.STARTS)}
              >
                Starts
              </SortableTableHead>
              <SortableTableHead
                className="font-semibold"
                direction={sortDirection(CustomerSortColumn.EXPIRES)}
                onSort={() => sortBy(CustomerSortColumn.EXPIRES)}
              >
                Expires
              </SortableTableHead>
              {canDelete && <TableHead className="w-12" />}
            </TableRow>
          </TableHeader>
          <TableBody inert={staleRows} className={staleRowsClass}>
            {isLoading && <TableSkeletonRows columns={columnCount} />}
            {!isLoading && organisations.length === 0 && (
              <TableRow>
                <TableCell colSpan={columnCount} className="pt-5 text-center">
                  No customers found
                </TableCell>
              </TableRow>
            )}
            {organisations.map((org) => (
              <TableRow
                key={org.id}
                className="cursor-pointer"
                onClick={(e) => handleRowClick(e, org.id)}
                onAuxClick={(e) => {
                  if (e.button === 1) {
                    e.preventDefault()
                    openCustomerInNewTab(org.id)
                  }
                }}
              >
                {/* Wraps so a long name stays inside its column instead of scrolling the table sideways. */}
                <TableCell className="font-medium break-words whitespace-normal">{org.name}</TableCell>
                <TableCell>
                  <StatusPill status={orgPillStatus(org)} />
                </TableCell>
                <TableCell>{org.userCount ?? 0}</TableCell>
                <CustomerPolicyCells policies={org.policies ?? []} />
                {canDelete && (
                  // Unpadded so the button fits inside the row rather than making it taller than the other tab's.
                  <TableCell className="py-0 text-right" onClick={(e) => e.stopPropagation()}>
                    <DeleteCustomerButton orgId={org.id} orgName={org.name} onDelete={handleDelete} />
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {(hasPreviousPage || hasNextPage) && (
          <div className="mt-4 flex items-center justify-between">
            <div className="text-muted-foreground text-sm">
              Page {page} ({count} total)
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!hasPreviousPage}
                onClick={() => goToPage(page - 1)}
              >
                Previous
              </Button>
              <Button variant="outline" size="sm" disabled={!hasNextPage} onClick={() => goToPage(page + 1)}>
                Next
              </Button>
            </div>
          </div>
        )}
      </CustomerListCard>

      <AddCustomerDialog open={addDialogOpen} onOpenChange={setAddDialogOpen} />
    </>
  )
}
